/**
 * „BK-Verfügbarkeit (Dispo)" — laufende und kommende Verfügbarkeits-Einträge
 * der Betreuungskräfte (Verfügbarkeit / Urlaub; „Kein Kontakt" seit 11.10.2026 nicht mehr), filterbar
 * nach Art. Die Einträge werden im BK-Profil in der Jahresübersicht gepflegt.
 *
 * Datenquelle: GET /v2/admin/bk-availability (scope=open). Das Widget lädt
 * selbst und meldet die Anzahl für den Kartentitel.
 */
import React, { useEffect, useState } from 'react';
import { BRAND, getThemeColors } from '../../../../lib/theme';
import { AppLink } from '../../../../lib/openLink';
import {
  BK_AVAILABILITY_SELECTABLE, isAbsenceType, v2BkAvailability,
  type BkAvailabilityEntry, type BkAvailabilityType,
} from '../../../../lib/bkAvailability';
import { BkAvailabilityBadge } from '../../../betreuung/BkAvailabilityBadge';

interface BkAvailabilityWidgetProps {
  mode: 'full' | 'summary';
  dark: boolean;
  tr: (k: string, opts?: Record<string, unknown>) => string;
  /** Meldet die Anzahl der Einträge für den Kartentitel. */
  onTotal?: (total: number | null) => void;
}

const SUMMARY_ROWS = 5;

export function BkAvailabilityWidget({ mode, dark, tr, onTotal }: BkAvailabilityWidgetProps) {
  const t = getThemeColors(dark);
  const [filter, setFilter] = useState<BkAvailabilityType | 'all'>('all');
  const [entries, setEntries] = useState<BkAvailabilityEntry[] | null>(null);
  const [error, setError] = useState(false);

  useEffect(() => {
    let alive = true;
    v2BkAvailability.list({ scope: 'open', limit: 500 })
      .then(rows => { if (alive) { setEntries(rows); setError(false); onTotal?.(rows.length); } })
      .catch(() => { if (alive) { setError(true); onTotal?.(null); } });
    return () => { alive = false; };
  }, [onTotal]);

  const all = entries ?? [];
  // „Abwesenheit" umfasst auch den Altbestand Urlaub.
  const matches = (e: BkAvailabilityEntry, type: BkAvailabilityType) => (type === 'abwesenheit' ? isAbsenceType(e.type) : e.type === type);
  const filtered = filter === 'all' ? all : all.filter(e => matches(e, filter));
  const rows = mode === 'summary' ? filtered.slice(0, SUMMARY_ROWS) : filtered;
  const count = (type: BkAvailabilityType) => all.filter(e => matches(e, type)).length;

  const chips: { type: BkAvailabilityType | 'all'; label: string; color: string; n: number }[] = [
    { type: 'all', label: tr('betreuung.availability.filter_all'), color: BRAND, n: all.length },
    ...BK_AVAILABILITY_SELECTABLE.map(d => ({ type: d.type, label: tr(d.labelKey), color: d.color, n: count(d.type) })),
  ];

  return (
    <div style={{ display: 'flex', flexDirection: 'column', minHeight: '100%' }}>
      <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', paddingBottom: 8, borderBottom: `1px solid ${t.borderSoft}` }}>
        {chips.map(c => {
          const active = filter === c.type;
          return (
            <button
              key={c.type}
              type="button"
              data-testid={`bk-availability-chip-${c.type}`}
              aria-pressed={active}
              onClick={e => { e.stopPropagation(); setFilter(c.type); }}
              style={{
                fontSize: 11.5, fontWeight: 600, padding: '4px 10px', borderRadius: 999, cursor: 'pointer',
                fontFamily: 'Outfit,sans-serif',
                border: `1px solid ${active ? c.color : t.border}`,
                background: active ? `${c.color}1a` : 'transparent',
                color: active ? c.color : t.text,
              }}
            >
              {c.label} <span style={{ fontWeight: 700 }}>{c.n}</span>
            </button>
          );
        })}
      </div>

      {entries == null ? (
        <div style={{ fontSize: 12, color: t.muted, paddingTop: 10 }}>
          {error ? tr('dashboard.widget.load_error') : tr('dashboard.widget.loading')}
        </div>
      ) : rows.length === 0 ? (
        <div style={{ padding: '12px 0', fontSize: 12, color: t.muted }}>{tr('dashboard.widget.bk_availability.empty')}</div>
      ) : (
        <div>
          {rows.map((e, i) => (
            <div
              key={e.id}
              data-testid="bk-availability-row"
              style={{
                display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 10, flexWrap: 'wrap',
                padding: '8px 0', borderBottom: i < rows.length - 1 ? `1px solid ${t.borderSoft}` : 'none',
              }}
            >
              <AppLink
                page="betreuung"
                sub={String(e.user_id)}
                stopPropagation
                style={{
                  textDecoration: 'none', cursor: 'pointer', textAlign: 'left',
                  fontSize: 12.5, fontWeight: 600, color: BRAND, minWidth: 0,
                  whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis',
                }}
              >
                {e.name || `BK #${e.user_id}`}
              </AppLink>
              <BkAvailabilityBadge entry={e} dark={dark} />
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
